import ClubAmenities from "@/components/ClubAmenities";
import GolfIcon from "@/components/GolfIcon";
import HorizontalScrollMedia from "@/components/HorizontalScrollMedia";
import { Badge } from "@/components/ui/badge";
import NeoCarousel from "@/components/ui/neoCarousel";
import { capitalizeCityName } from "@/lib/utils";
import { api } from "@/utils/api";
import { saveVisitedClub } from "@/utils/recentClubs";
import { IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonPage, IonTitle, IonToolbar } from "@ionic/react";
import { call, compass, compassOutline, createOutline, earthOutline, heartOutline, shareOutline } from "ionicons/icons";
import { Globe, Mail, MapPin, Phone } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

const Club: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<any>(null);
  // Sample media items for the course
  const coursesMedias = [{ type: 'image', url: 'https://pub-d8b8f775e92f432cb957c6b4d3d755aa.r2.dev/96/000001.jpg' }, { type: 'image', url: 'https://pub-d8b8f775e92f432cb957c6b4d3d755aa.r2.dev/96/000002.jpg' }, { type: 'image', url: 'https://pub-d8b8f775e92f432cb957c6b4d3d755aa.r2.dev/96/000003.jpg' }];
  const { t } = useTranslation();
  useEffect(() => {
    if (!courseId) return;

    const fetchCourse = async () => {
      try {
        const response = await api.get(`/golf-clubs/${courseId}`);
        const clubData = response.data;
        setCourse(clubData);

        // Save this club to recently visited clubs
        await saveVisitedClub(clubData);
      } catch (error) {
        console.error("Error fetching course:", error);
      }
    };

    // Note: Course photos functionality will be implemented in a future update

    fetchCourse();
    // fetchCoursePhotos();
  }, [courseId]);

  if (!course) {
    return (
      <div className="flex justify-center items-center h-screen text-lg text-gray-500">
        Loading...
      </div>
    );
  }

  return (
    <IonPage>
      <IonHeader className="md:hidden">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton text='' defaultHref="/dashboard" />
          </IonButtons>
          <IonTitle>{course.name}</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear"><IonIcon icon={heartOutline} /></IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {/* Hero Section */}
        <div className="w-full">
          <NeoCarousel
            images={coursesMedias}
            className="w-full h-full max-h-[512px] min-h-[300px] object-contain"
          />
        </div>
        <div className="p-2 max-w-4xl mx-auto">
          <div className="inset-0 z-20 flex flex-col justify-end">
            <h1 className="text-2xl font-bold">{course.name}</h1>
            <p className="text-sm">
              {capitalizeCityName(course.city)}, {capitalizeCityName(course.country)}
            </p>
          </div>
          <HorizontalScrollMedia disableArrows>
            <IonButton shape="round" fill="solid" color="primary">
              <IonIcon slot="start" icon={compassOutline}></IonIcon>
              {t('play')}
            </IonButton>
            {course.address && (
              <IonButton target="_blank" href={`https://www.google.com/maps/search/?api=1&query=${course.name}, ${course.address}, ${course.city}`} shape="round" fill="solid" color="transparent" className="bg-green-50 dark:bg-green-900/50 text-foreground hover:bg-green-100 dark:hover:bg-green-900/40 rounded-full text-green-600 dark:text-green-400">
                <GolfIcon name="navigation" />
                {t('itinerary')}
              </IonButton>
            )}
            {course.phone && (
              <a href={`tel:${course.phone}`}>
                <IonButton shape="round" fill="solid" color="transparent" className="bg-green-50 dark:bg-green-900/50 text-foreground hover:bg-green-100 dark:hover:bg-green-900/40 rounded-full text-green-600 dark:text-green-400">
                  <IonIcon slot="start" icon={call}></IonIcon>
                  {t('call')}
                </IonButton>
              </a>
            )}
            {course.websiteUrl && (
              <IonButton href={"https://" + course.websiteUrl} target="_blank" shape="round" fill="solid" color="transparent" className="bg-green-50 dark:bg-green-900/50 text-foreground hover:bg-green-100 dark:hover:bg-green-900/40 rounded-full text-green-600 dark:text-green-400">
                <IonIcon slot="start" icon={earthOutline}></IonIcon>
                {t('website')}
              </IonButton>
            )}
            {/* <IonButton routerLink={`/clubs/${courseId}/edit`} shape="round" fill="solid" color="transparent" className="bg-green-50 dark:bg-green-900/50 text-foreground hover:bg-green-100 dark:hover:bg-green-900/40 rounded-full text-green-600 dark:text-green-400">
              <IonIcon slot="start" icon={createOutline}></IonIcon>
              {t('edit')}
            </IonButton> */}
          </HorizontalScrollMedia>
          {/* Main Content */}
          <section className="mb-8">
            <div className="grid grid-cols-2 gap-4 md:gap-8">
              {course.architect && (
                <div>
                  <p className="text-sm text-gray-500">Architecte</p>
                  <p className="text-lg font-semibold">{course.architect}</p>
                </div>
              )}
              {course.holes && (
                <Badge color="secondary" className="pointer-events-none py-2 rounded-full w-fit gap-2 text-white">
                  <img src="/assets/images/icons/golf-hole.svg" className='size-6 dark:invert' />
                  {course.holes} trous
                </Badge>
              )}
            </div>
          </section>

          {/* Description */}
          <section className="mb-8">
            <p className="text-base leading-relaxed">
              {course.description || "Aucune description disponible pour ce club."}
            </p>
          </section>
          <hr />

          {/* Features & Amenities */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Équipements et Services</h2>
            <ClubAmenities
              drivingRange={course.drivingRange}
              puttingGreen={course.puttingGreen}
              chippingGreen={course.chippingGreen}
              practiceBunker={course.practiceBunker}
              motorCart={course.motorCart}
              pullCart={course.pullCart}
              golfClubsRental={course.golfClubsRental}
              clubFitting={course.clubFitting}
              proShop={course.proShop}
              golfLessons={course.golfLessons}
              caddieHire={course.caddRingaieHire}
              restaurant={course.restaurant}
              receptionHall={course.receptionHall}
              changingRoom={course.changingRoom}
              lockers={course.lockers}
              lodgingOnSite={course.lodgingOnSite}
            />
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Informations de Contact</h2>
            <div className="space-y-4">
              {course.address && (
                <div className="flex items-center">
                  <MapPin className="mr-2 text-gray-500" />
                  <p>
                    {course.address}, {capitalizeCityName(course.city)}, {course.zip}
                  </p>
                </div>
              )}
              {course.phone && (
                <div className="flex items-center">
                  <Phone className="mr-2 text-gray-500" />
                  <p>{course.phone}</p>
                </div>
              )}
              {course.email && (
                <div className="flex items-center">
                  <Mail className="mr-2 text-gray-500" />
                  <a href={`mailto:${course.email}`} className="text-blue-600 hover:underline">
                    {course.email}
                  </a>
                </div>
              )}
              {course.websiteUrl && (
                <div className="flex items-center text-gray-700">
                  <Globe className="mr-2 text-gray-500" />
                  <a
                    href={course.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {course.websiteUrl}
                  </a>
                </div>
              )}
            </div>
          </section>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Club;